import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ordersApi, invoicesApi, paymentsApi } from '../services/api';

const STATUS_LABELS = {
  waiting_payment: 'Čeká na platbu', paid: 'Zaplaceno', packed: 'Zabaleno',
  shipped: 'Odesláno', pickup_ready: 'Vyzvednutí', cancelled: 'Zrušeno',
};
const STATUS_FLOW = ['waiting_payment', 'paid', 'packed', 'shipped'];

function fmt(n) {
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n || 0);
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', note: '' });
  const [msg, setMsg] = useState('');

  function load() {
    ordersApi.get(id).then(setOrder).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [id]);

  async function setStatus(status) {
    await ordersApi.updateStatus(id, status);
    setMsg('Status aktualizován'); load();
  }

  async function addPayment(e) {
    e.preventDefault();
    await paymentsApi.create({ order_id: parseInt(id), ...paymentForm, amount: parseFloat(paymentForm.amount) });
    setMsg('Platba přidána'); setPaymentForm({ amount: '', method: 'cash', note: '' }); load();
  }

  async function sendInvoice(invId) {
    await invoicesApi.send(invId);
    setMsg('Faktura odeslána emailem');
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!order) return <div className="empty-state">Objednávka nenalezena</div>;

  const isPaid = order.paid_amount >= order.total_amount;
  const statusIdx = STATUS_FLOW.indexOf(order.status);

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/orders" style={{ color: 'var(--text-muted)', fontSize: 13 }}>← Zpět</Link>
          <h1 className="page-title" style={{ marginTop: 4 }}>{order.order_number}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {STATUS_FLOW.filter((_, i) => i > statusIdx && i <= statusIdx + 1).map(s => (
            <button key={s} className="btn btn-primary" onClick={() => setStatus(s)}>
              → {STATUS_LABELS[s]}
            </button>
          ))}
          {order.status !== 'cancelled' && (
            <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm('Zrušit objednávku?')) setStatus('cancelled'); }}>
              Zrušit
            </button>
          )}
        </div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}

      {/* Status progress */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_FLOW.map((s, i) => (
          <div key={s} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: i <= statusIdx ? 'var(--blue)' : 'var(--border-light)',
            color: i <= statusIdx ? 'white' : 'var(--text-muted)',
          }}>
            {STATUS_LABELS[s]}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Customer */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>👤 Zákazník</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{order.customer_name}</div>
          {order.customer_email && <div style={{ fontSize: 13 }}>📧 <a href={`mailto:${order.customer_email}`}>{order.customer_email}</a></div>}
          {order.customer_phone && <div style={{ fontSize: 13 }}>📱 {order.customer_phone}</div>}
          {order.customer_address && (
            <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text-muted)' }}>
              📍 {order.customer_address}, {order.customer_zip} {order.customer_city}
            </div>
          )}
        </div>

        {/* Order info */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>📦 Informace</div>
          <table style={{ width: '100%', fontSize: 13 }}>
            <tbody>
              <tr><td style={{ color: 'var(--text-muted)', padding: '3px 0' }}>Status:</td><td><strong>{STATUS_LABELS[order.status]}</strong></td></tr>
              <tr><td style={{ color: 'var(--text-muted)', padding: '3px 0' }}>Doručení:</td><td>{order.delivery_method === 'post' ? 'Pošta' : order.delivery_method === 'pickup' ? 'Vyzvednutí' : 'Osobně'}</td></tr>
              <tr><td style={{ color: 'var(--text-muted)', padding: '3px 0' }}>Splatnost:</td><td style={{ color: !isPaid && order.due_date < new Date().toISOString().slice(0,10) ? 'var(--red)' : '' }}>{order.due_date}</td></tr>
              <tr><td style={{ color: 'var(--text-muted)', padding: '3px 0' }}>Zdroj:</td><td>{order.source}</td></tr>
              {order.tracking_number && <tr><td style={{ color: 'var(--text-muted)', padding: '3px 0' }}>Tracking:</td><td><a href={`https://www.postaonline.cz/trackandtrace/-/zasilka/cislo/${order.tracking_number}`} target="_blank" rel="noreferrer">{order.tracking_number}</a></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>🛒 Položky</div>
        <table>
          <thead><tr><th>Produkt</th><th>Varianta</th><th style={{ textAlign: 'right' }}>Ks</th><th style={{ textAlign: 'right' }}>Cena/ks</th><th style={{ textAlign: 'right' }}>Celkem</th></tr></thead>
          <tbody>
            {order.items?.map(item => (
              <tr key={item.id}>
                <td>{item.product_name}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{item.variant}</td>
                <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                <td style={{ textAlign: 'right' }}>{fmt(item.price_unit)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(item.price_total)}</td>
              </tr>
            ))}
            {order.shipping_cost > 0 && (
              <tr><td colSpan={4} style={{ color: 'var(--text-muted)' }}>Poštovné</td><td style={{ textAlign: 'right' }}>{fmt(order.shipping_cost)}</td></tr>
            )}
          </tbody>
        </table>
        <div style={{ borderTop: '2px solid var(--border)', paddingTop: 10, marginTop: 8, textAlign: 'right' }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>CELKEM: {fmt(order.total_amount)}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Payments */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>💳 Platby</div>
          {order.payments?.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
              <span>{p.payment_date?.slice(0,10)} — {p.method}</span>
              <strong style={{ color: 'var(--green)' }}>{fmt(p.amount)}</strong>
            </div>
          ))}
          <div style={{ marginTop: 12, padding: '8px 0', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
            <span>Zaplaceno:</span><span style={{ color: isPaid ? 'var(--green)' : 'var(--red)' }}>{fmt(order.paid_amount)}</span>
          </div>
          {!isPaid && (
            <form onSubmit={addPayment} style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input className="form-control" style={{ width: 100 }} type="number" step="0.01" placeholder="Částka" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({...f, amount: e.target.value}))} required />
              <select className="form-control" style={{ width: 100 }} value={paymentForm.method} onChange={e => setPaymentForm(f => ({...f, method: e.target.value}))}>
                <option value="cash">Hotovost</option><option value="bank">Banka</option><option value="card">Karta</option>
              </select>
              <button className="btn btn-success btn-sm" type="submit">Přidat platbu</button>
            </form>
          )}
        </div>

        {/* Invoices */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>📄 Faktury</div>
          {order.invoices?.map(inv => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{inv.invoice_number}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inv.type} — {fmt(inv.total)}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <a href={invoicesApi.pdfUrl(inv.id)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">PDF</a>
                <button className="btn btn-primary btn-sm" onClick={() => sendInvoice(inv.id)}>📧 Poslat</button>
              </div>
            </div>
          ))}
          {(!order.invoices || order.invoices.length === 0) && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Žádné faktury</div>}
        </div>
      </div>

      {order.notes && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>📝 Poznámka</div>
          <p style={{ fontSize: 13 }}>{order.notes}</p>
        </div>
      )}
    </div>
  );
}
